import React, { useState } from 'react';
import BlurFade from '../primitives/BlurFade';
import Pill from '../ui/Pill';
import { Section } from '../layout/Section';
import { Container } from '../layout/Container';
import { Eyebrow, Headline, Body } from '../layout/typography';
import { MessageCircle, Phone, ArrowRight } from 'lucide-react';
import { submitLead } from '../../lib/api';

/**
 * LeadFormBlock — Act VII (Decision). Lead capture with escalating clarity.
 */
export default function LeadFormBlock({
  tone = 'sand', rhythm = 'grand', eyebrow, headline, sub,
  fields, submitLabel, success, error, alternates = [], lang = 'nl',
  source = 'cta-block', extraNote = null,
}) {
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [status, setStatus] = useState('idle');

  const onChange = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const onSubmit = async (e) => {
    e.preventDefault(); setStatus('loading');
    try {
      await submitLead({
        ...form,
        phone: form.phone || null,
        language: lang,
        source,
        message: extraNote || undefined,
      });
      setStatus('success'); setForm({ name: '', email: '', phone: '' });
    } catch (err) { console.error(err); setStatus('error'); }
  };

  const altIcon = (kind) => kind === 'whatsapp' ? <MessageCircle className="w-4 h-4" /> : <Phone className="w-4 h-4" />;

  return (
    <Section id="cta-block" data-testid="cta-block-section" tone={tone} rhythm={rhythm}>
      <Container variant="narrow">
        <BlurFade><Eyebrow className="mb-7">{eyebrow}</Eyebrow></BlurFade>
        <BlurFade delay={0.05}><Headline size="xl" className="max-w-3xl">{headline}</Headline></BlurFade>
        <BlurFade delay={0.1}><Body className="mt-6 max-w-md">{sub}</Body></BlurFade>

        <BlurFade delay={0.2} as="form" onSubmit={onSubmit} data-testid="cta-block-form"
          className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-3">
          <input required placeholder={fields.name} value={form.name} onChange={onChange('name')}
            data-testid="cta-block-name" autoComplete="name" className="glass-field md:col-span-1" />
          <input required type="email" placeholder={fields.email} value={form.email} onChange={onChange('email')}
            data-testid="cta-block-email" autoComplete="email" className="glass-field md:col-span-1" />
          <input placeholder={fields.phone} value={form.phone} onChange={onChange('phone')}
            data-testid="cta-block-phone" autoComplete="tel" inputMode="tel" className="glass-field md:col-span-1" />
          <Pill variant="leaf" type="submit" disabled={status === 'loading'} testId="cta-block-submit"
            className="md:col-span-1">
            {status === 'loading' ? fields.submitting : submitLabel}
          </Pill>
        </BlurFade>

        <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4 text-[12.5px] text-[#5C5852]">
          {alternates.map((a, i) => (
            <React.Fragment key={`${a.kind}-${a.href || i}`}>
              {i > 0 && <span className="hidden sm:inline opacity-50">·</span>}
              <a href={a.href} {...(a.kind === 'whatsapp' ? { target: '_blank', rel: 'noreferrer' } : {})}
                className="inline-flex items-center gap-2 link-underline">
                {altIcon(a.kind)} {a.label}
              </a>
            </React.Fragment>
          ))}
        </div>

        {status === 'success' && (
          <p data-testid="cta-block-success" className="mt-6 text-[#F26B1A] text-[14px] font-medium">{success}</p>
        )}
        {status === 'error' && (
          <p data-testid="cta-block-error" className="mt-6 text-red-600 text-[14px]">{error}</p>
        )}
      </Container>
    </Section>
  );
}
